export interface CritiqueData {
  score: number
  feedback: string | string[] | Record<string, unknown> | null
}

function parseFeedbackBullets(feedback: CritiqueData['feedback']): string[] {
  if (!feedback) return []
  if (typeof feedback === 'string') return feedback ? [feedback] : []
  if (Array.isArray(feedback)) return feedback.map(String).filter(Boolean)
  // Object: collect string leaf values (skip numeric score keys)
  return Object.entries(feedback)
    .filter(([, v]) => typeof v === 'string')
    .map(([, v]) => String(v))
}

function getScoreColors(score: number): { ring: string; text: string } {
  if (score >= 8) return { ring: '#10b981', text: 'text-emerald-600' }
  if (score >= 6) return { ring: '#f59e0b', text: 'text-amber-600' }
  return { ring: '#ef4444', text: 'text-red-600' }
}

interface CritiqueCardProps {
  data: CritiqueData
}

export function CritiqueCard({ data }: CritiqueCardProps) {
  const { score, feedback } = data
  const bullets = parseFeedbackBullets(feedback)
  const normalized = Math.min(Math.max(score, 0), 10)
  const colors = getScoreColors(normalized)

  const radius = 28
  const strokeWidth = 5
  const center = radius + strokeWidth
  const svgSize = center * 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (normalized / 10) * circumference

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {/* Circular score badge */}
        <div className="relative inline-flex shrink-0 items-center justify-center">
          <svg width={svgSize} height={svgSize} className="-rotate-90" aria-hidden>
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.ring}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-base font-bold leading-none ${colors.text}`}>
              {normalized.toFixed(1)}
            </span>
            <span className="text-[9px] leading-none text-slate-400">/10</span>
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-snug">Evaluacion de calidad del contenido</p>
      </div>

      {bullets.length > 0 && (
        <ul className="space-y-1.5">
          {bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
              <span className="leading-snug">{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
